import React from 'react';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BQ_BASE_URL = process.env.REACT_APP_BQ_BASE_URL;

const ratingLabels = [
  { value: 1, label: 'Poor', competitor: 'much_Worse' },
  { value: 2, label: 'Fair', competitor: 'worse' },
  { value: 3, label: 'Good', competitor: 'same' },
  { value: 4, label: 'Very Good', competitor: 'better' },
  { value: 5, label: 'Excellent', competitor: 'much_better' },
];

const PillToggle = ({ label, selected, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className={`px-4 py-1 rounded-full border text-sm font-medium transition ${
      selected
        ? 'bg-black text-white border-black'
        : 'bg-white text-gray-700 border-gray-400 hover:bg-gray-200'
    }`}
  >
    {label}
  </button>
);

const YesNoPill = ({ name, value, onChange }) => (
  <div className="flex space-x-2">
    {['Yes', 'No'].map((opt) => (
      <PillToggle
        key={opt}
        label={opt}
        selected={(opt === 'Yes') === value}
        onClick={() => onChange({ target: { name, value: opt === 'Yes' } })}
      />
    ))}
  </div>
);

export const GuestFeedback = ({ onClose, orderId, guestName, guestEmail }) => {
  const [form, setForm] = React.useState({
    order_id: orderId || '',
    name: guestName || '',
    email: guestEmail || '',
    message: '',
    overall_rating: 5,
    cleanliness_rating: 5,
    value_for_money_rating: 5,
    service_rating: 5,
    location_rating: 5,
    would_return: true,
    would_recommend: true,
    is_contact_allowed: true,
  });
  const [competitorComparison, setCompetitorComparison] = React.useState('Much Better');
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => {
    const comparison = ratingLabels.find((r) => r.value === form.overall_rating)?.competitor;
    setCompetitorComparison(comparison);
  }, [form.overall_rating]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${BQ_BASE_URL}/bq/api/feedback/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...form,
          compared_to_competitors: competitorComparison,
        }),
      });

      if (!res.ok) throw new Error('Failed to submit feedback');

      toast.success('Thank you for your valuable feedback!');
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to submit feedback');
    } finally {
      setLoading(false);
    }
  };

  const renderRating = (label, name, value) => (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1">{label}</label>
      <div className="flex gap-2">
        {ratingLabels.map((r) => (
          <PillToggle
            key={r.value}
            label={r.label}
            selected={value === r.value}
            onClick={() => handleChange({ target: { name, value: r.value } })}
          />
        ))}
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-start mb-4">
            <h2 className="text-2xl font-bold text-black">Guest Feedback</h2>
            <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Contact Fields */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">
                  Reservation ID
                </label>
                <input
                  name="order_id"
                  value={form.order_id}
                  onChange={handleChange}
                  required
                  readOnly
                  className="px-3 w-full py-2 border border-gray-300 rounded-lg placeholder:text-sm outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">Name</label>
                <input
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  className="px-3 py-2 w-full border border-gray-300 rounded-lg placeholder:text-sm outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-600 mb-1">Email</label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  className="px-3 py-2 w-full border border-gray-300 rounded-lg placeholder:text-sm outline-none"
                />
              </div>
            </div>

            {/* Overall Rating */}
            {/* <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Overall Experience
              </label>
              <div className="flex gap-2 flex-wrap">
                {ratingLabels.map((r) => (
                  <PillToggle
                    key={r.value}
                    label={r.label}
                    selected={form.overall_rating === r.value}
                    onClick={() =>
                      handleChange({
                        target: { name: "overall_rating", value: r.value },
                      })
                    }
                  />
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Compared to competitors: <strong>{competitorComparison}</strong>
              </p>
            </div> */}

            {/* Category Ratings */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 whitespace-nowrap">
              {renderRating('Cleanliness', 'cleanliness_rating', form.cleanliness_rating)}
              {renderRating(
                'Value for Money',
                'value_for_money_rating',
                form.value_for_money_rating
              )}
              {renderRating('Service', 'service_rating', form.service_rating)}
              {renderRating('Location', 'location_rating', form.location_rating)}
            </div>

            {/* Text Feedback */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">
                Additional Feedback
              </label>
              <textarea
                name="message"
                rows={4}
                placeholder="Tell us more about your experience..."
                value={form.message}
                onChange={handleChange}
                className="w-full p-3 rounded border border-gray-300 resize-y"
              />
            </div>

            {/* Toggles */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Would you return?
                </label>
                <YesNoPill name="would_return" value={form.would_return} onChange={handleChange} />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Would you recommend us?
                </label>
                <YesNoPill
                  name="would_recommend"
                  value={form.would_recommend}
                  onChange={handleChange}
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">
                  Can we contact you?
                </label>
                <YesNoPill
                  name="is_contact_allowed"
                  value={form.is_contact_allowed}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-black text-white rounded-lg font-medium hover:bg-gray-800 disabled:opacity-50"
              >
                {loading ? 'Submitting...' : 'Submit Feedback'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};
